import React, {useEffect, useState} from 'react';
import {ChevronDownIcon, SearchIcon} from './icons';
import {PURPOSE_OPTIONS} from '../support/itemLifecycle';
import {ListFilterKey, ListFilters} from '../support/catalogFilters';

/** Значение справочника в панели фильтров. `count` бэкенд пока не отдаёт — рисуем, если появится. */
export interface RefOption {
    id: string;
    code: string;
    nameRu: string;
    count?: number;
}

export interface FilterRefs {
    categories: RefOption[];
    colors: RefOption[];
    sizes: RefOption[];
    layers: RefOption[];
    seasons: RefOption[];
    warmthLevels: RefOption[];
    materials: RefOption[];
    occasions: RefOption[];
    styles: RefOption[];
    conditions: RefOption[];
    weatherSuitabilities: RefOption[];
    brands: RefOption[];
}

export const EMPTY_FILTER_REFS: FilterRefs = {
    categories: [], colors: [], sizes: [], layers: [], seasons: [], warmthLevels: [],
    materials: [], occasions: [], styles: [], conditions: [], weatherSuitabilities: [], brands: [],
};

export interface CatalogFiltersProps {
    refs: FilterRefs;
    lists: ListFilters;
    onToggle: (key: ListFilterKey, value: string) => void;
    hasDefects: boolean;
    waterProof: boolean | null;
    /** «Только избранные» (CTHS-273): включён либо не задан — обратного фильтра в UI нет. */
    favorite: boolean;
    onToggleDefects: () => void;
    onToggleWaterProof: () => void;
    onToggleFavorite: () => void;
}

// Approximate hex mapping for color codes
const COLOR_HEX: Record<string, string> = {
    BLACK: '#1a1a1a', WHITE: '#f5f5f5', GRAY: '#9ca3af', GREY: '#9ca3af',
    RED: '#ef4444', BLUE: '#3b82f6', GREEN: '#22c55e', YELLOW: '#eab308',
    ORANGE: '#f97316', PINK: '#ec4899', PURPLE: '#a855f7', BROWN: '#92400e',
    BEIGE: '#d2b48c', NAVY: '#1e3a5f', KHAKI: '#c3b091', OLIVE: '#6b7c3d',
    TURQUOISE: '#06b6d4', BURGUNDY: '#9b1c31', CREAM: '#fffdd0', MINT: '#98ff98',
    CORAL: '#ff7f50', LAVENDER: '#e6e6fa', TEAL: '#008080', INDIGO: '#4b0082',
};

/** Сколько категорий видно до «Показать ещё». */
const VISIBLE_CATEGORIES = 6;

interface FilterSectionProps {
    title: string;
    count: number;
    children: React.ReactNode;
}

/**
 * Секция-аккордеон панели фильтров.
 *
 * Секция с активным отбором раскрыта и помечена числом (C9): двенадцать одинаковых
 * закрытых заголовков не давали понять, где именно стоит фильтр.
 */
const FilterSection: React.FC<FilterSectionProps> = ({title, count, children}) => {
    const active = count > 0;
    const [open, setOpen] = useState(active);
    // Фильтр этой секции включили извне (ссылка, быстрый чип) — раскрываем, чтобы
    // было видно, что именно выбрано. Закрывать сами не спешим: свернул — значит свернул.
    useEffect(() => {
        if (active) setOpen(true);
    }, [active]);

    return (
        <div data-testid="filter-section" data-filter-title={title} data-filter-active={active}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                className="w-full flex items-center justify-between py-2.5 text-left group"
            >
                <span className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                    active
                        ? 'text-gray-700 dark:text-gray-200'
                        : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'
                }`}>
                    {title}
                </span>
                <span className="flex items-center gap-1.5">
                    {active && (
                        <span data-testid="filter-section-badge"
                            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-white text-[10px] font-bold leading-none">
                            {count}
                        </span>
                    )}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        className={`text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-[color,transform] duration-ui ease-out ${open ? 'rotate-180' : ''}`}>
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </span>
            </button>
            {open && <div className="pb-3">{children}</div>}
        </div>
    );
};

interface FilterToggleProps {
    label: string;
    checked: boolean;
    onChange: () => void;
    testId: string;
}

/**
 * Тумблер булева фильтра. Один компонент на все переключатели панели: раньше разметка
 * копировалась под каждый, и третий («Только избранные») копировался бы снова.
 *
 * Под визуальной дорожкой лежит настоящий `checkbox` — тумблер должен доставаться
 * с клавиатуры и читаться скринридером, а не быть парой `div` с `onClick`.
 */
const FilterToggle: React.FC<FilterToggleProps> = ({label, checked, onChange, testId}) => (
    <label className={`flex items-center gap-2.5 min-h-[44px] lg:min-h-0 cursor-pointer select-none ${
        checked ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
    }`}>
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            data-testid={testId}
            className="sr-only peer"
        />
        <span aria-hidden
            className={`w-8 h-4 rounded-full transition-colors relative shrink-0 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400 ${
                checked ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
            }`}>
            <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-ui ease-out ${
                checked ? 'translate-x-4' : ''
            }`}/>
        </span>
        <span className="text-xs font-medium">{label}</span>
    </label>
);

/** Chip-кнопка значения фильтра. */
const chipCls = (active: boolean) =>
    `px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer select-none pressable ${
        active
            ? 'bg-primary-500 text-white border-primary-500'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-primary-400 dark:hover:border-primary-600 hover:text-primary-600 dark:hover:text-primary-400'
    }`;

/**
 * Панель фильтров каталога: одна разметка на десктопный сайдбар и мобильную шторку.
 * Состояние отбора приходит сверху — панель ничего не хранит, кроме собственного
 * раскрытия секций и поиска по категориям.
 */
const CatalogFilters: React.FC<CatalogFiltersProps> = ({
    refs, lists, onToggle, hasDefects, waterProof, favorite,
    onToggleDefects, onToggleWaterProof, onToggleFavorite,
}) => {
    const [categorySearch, setCategorySearch] = useState('');
    const [showAllCategories, setShowAllCategories] = useState(false);

    const chipSection = (title: string, key: ListFilterKey, options: RefOption[]) => (
        options.length > 0 && (
            <FilterSection title={title} count={lists[key].length}>
                <div className="flex flex-wrap gap-1.5">
                    {options.map(r => (
                        <button key={r.id} type="button" onClick={() => onToggle(key, r.id)}
                            data-testid={`filter-chip-${key}-${r.id}`}
                            aria-pressed={lists[key].includes(r.id)}
                            className={chipCls(lists[key].includes(r.id))}>
                            {r.nameRu}
                            {r.count != null && <span className="ml-1 tabular-nums opacity-70">{r.count}</span>}
                        </button>
                    ))}
                </div>
            </FilterSection>
        )
    );

    const filteredCategories = refs.categories.filter(r =>
        !categorySearch || r.nameRu.toLowerCase().includes(categorySearch.toLowerCase())
    );
    const visibleCategories = showAllCategories ? filteredCategories : filteredCategories.slice(0, VISIBLE_CATEGORIES);

    return (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">

            {/* Категория — чекбоксы с поиском */}
            {refs.categories.length > 0 && (
                <FilterSection title="Категория" count={lists.categoryIds.length}>
                    <div className="relative mb-2">
                        <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"/>
                        <input
                            type="text"
                            value={categorySearch}
                            onChange={e => setCategorySearch(e.target.value)}
                            placeholder="Поиск категории..."
                            aria-label="Поиск категории"
                            className="w-full pl-7 pr-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-ink-900 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                        />
                    </div>
                    <div className="space-y-1">
                        {visibleCategories.map(r => (
                            <label key={r.id} className="flex items-center gap-2 py-0.5 cursor-pointer group">
                                <input type="checkbox" checked={lists.categoryIds.includes(r.id)}
                                    data-testid={`filter-checkbox-categoryIds-${r.id}`}
                                    onChange={() => onToggle('categoryIds', r.id)}
                                    className="rounded w-4 h-4 accent-primary-600 cursor-pointer shrink-0"/>
                                <span className={`text-sm truncate ${lists.categoryIds.includes(r.id) ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100'}`}>
                                    {r.nameRu}
                                </span>
                                {r.count != null && (
                                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 tabular-nums">{r.count}</span>
                                )}
                            </label>
                        ))}
                    </div>
                    {filteredCategories.length > VISIBLE_CATEGORIES && (
                        <button type="button" onClick={() => setShowAllCategories(v => !v)}
                            className="mt-2 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline">
                            {showAllCategories ? 'Скрыть' : `Показать ещё ${filteredCategories.length - VISIBLE_CATEGORIES}`}
                            <ChevronDownIcon className={`w-3 h-3 transition-transform ${showAllCategories ? 'rotate-180' : ''}`}/>
                        </button>
                    )}
                </FilterSection>
            )}

            {/* Назначение — единственная секция без справочника: значения заданы в коде */}
            <FilterSection title="Назначение" count={lists.purposes.length}>
                <div className="flex flex-wrap gap-1.5">
                    {PURPOSE_OPTIONS.map(p => (
                        <button key={p.value} type="button" onClick={() => onToggle('purposes', p.value)}
                            data-testid={`filter-chip-purposes-${p.value.toLowerCase()}`}
                            aria-pressed={lists.purposes.includes(p.value)}
                            className={chipCls(lists.purposes.includes(p.value))}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </FilterSection>

            {/* Цвет — образцы, а не подписи */}
            {refs.colors.length > 0 && (
                <FilterSection title="Цвет" count={lists.colorIds.length}>
                    <div className="flex flex-wrap gap-2">
                        {refs.colors.map(r => {
                            const hex = COLOR_HEX[r.code?.toUpperCase()];
                            const active = lists.colorIds.includes(r.id);
                            return (
                                <button key={r.id} type="button" title={r.nameRu} aria-label={r.nameRu}
                                    data-testid={`filter-chip-colorIds-${r.id}`}
                                    aria-pressed={active}
                                    onClick={() => onToggle('colorIds', r.id)}
                                    className={`w-7 h-7 rounded-full transition-transform duration-ui ease-out ${active ? 'ring-2 ring-offset-2 ring-primary-500 dark:ring-offset-gray-900 scale-110' : 'hover:scale-110'}`}
                                    style={hex ? {backgroundColor: hex, border: hex === '#f5f5f5' ? '1px solid #d1d5db' : undefined} : undefined}>
                                    {!hex && <span className="text-[9px] leading-none">{r.nameRu.slice(0, 2)}</span>}
                                </button>
                            );
                        })}
                    </div>
                </FilterSection>
            )}

            {chipSection('Размер', 'sizeIds', refs.sizes)}
            {chipSection('Сезон', 'seasonIds', refs.seasons)}
            {chipSection('Состояние', 'conditionIds', refs.conditions)}
            {chipSection('Материал', 'materialIds', refs.materials)}
            {chipSection('Стиль', 'styleIds', refs.styles)}
            {chipSection('Повод', 'occasionIds', refs.occasions)}
            {chipSection('Слой', 'layerIds', refs.layers)}
            {chipSection('Погода', 'weatherSuitabilityIds', refs.weatherSuitabilities)}
            {chipSection('Бренд', 'brandIds', refs.brands)}

            {/* Тумблеры */}
            <div className="pt-3 space-y-2">
                {/* Избранное — первым: это срез «мои любимые вещи», а не свойство ткани */}
                <FilterToggle label="Только избранные" checked={favorite}
                    onChange={onToggleFavorite} testId="filter-favorite"/>
                <FilterToggle label="С дефектами" checked={hasDefects}
                    onChange={onToggleDefects} testId="filter-has-defects"/>
                <FilterToggle label="Водостойкое" checked={waterProof === true}
                    onChange={onToggleWaterProof} testId="filter-waterproof"/>
            </div>
        </div>
    );
};

export default CatalogFilters;
